import { Context, Data, Effect, Layer } from 'effect';

const TypeId = 'okrapdf/core/persistence/SqlExecutor/TypeId' as const;
const ErrorTypeId = 'okrapdf/core/persistence/SqlExecutor/SqlExecutorError' as const;

export type SqlBinding = string | number | bigint | null | ArrayBuffer | ArrayBufferView;
export type SqlRow = Readonly<Record<string, unknown>>;

/** The cursor surface shared by Durable Object SQLite and the Node adapter seam. */
export interface SqlCursorLike extends Iterable<unknown> {}

/**
 * Synchronous driver seam used by the existing DocumentAgent graph.
 * `makeFromSource` lifts it into the typed Effect requirement.
 */
export interface SqlExecutorSource {
  readonly exec: (query: string, ...bindings: SqlBinding[]) => SqlCursorLike;
}

export interface SqlExecutor {
  readonly [TypeId]: typeof TypeId;
  readonly exec: <Row extends SqlRow = SqlRow>(
    query: string,
    ...bindings: SqlBinding[]
  ) => Effect.Effect<ReadonlyArray<Row>, SqlExecutorError>;
  readonly one: <Row extends SqlRow = SqlRow>(
    query: string,
    ...bindings: SqlBinding[]
  ) => Effect.Effect<Row | undefined, SqlExecutorError>;
}

export type SqlExecutorMethod = 'exec' | 'one';

export class SqlExecutorError extends Data.TaggedError('SqlExecutorError')<{
  readonly message: string;
  readonly method: SqlExecutorMethod;
  readonly query: string;
  readonly cause?: unknown;
}> {
  readonly [ErrorTypeId]: typeof ErrorTypeId = ErrorTypeId;
}

export const SqlExecutor: Context.Service<SqlExecutor, SqlExecutor> = Context.Service(
  'okrapdf/core/persistence/SqlExecutor',
);

export interface SqlExecutorMakeOptions {
  readonly exec: <Row extends SqlRow = SqlRow>(
    query: string,
    ...bindings: SqlBinding[]
  ) => Effect.Effect<ReadonlyArray<Row>, SqlExecutorError>;
}

/** Construct a SqlExecutor from its row-returning primitive. */
export function make(options: SqlExecutorMakeOptions): SqlExecutor {
  return SqlExecutor.of({
    [TypeId]: TypeId,
    exec: options.exec,
    one: <Row extends SqlRow = SqlRow>(
      query: string,
      ...bindings: SqlBinding[]
    ): Effect.Effect<Row | undefined, SqlExecutorError> =>
      Effect.map(options.exec<Row>(query, ...bindings), (rows): Row | undefined => rows[0]),
  });
}

/** Lift the synchronous iterable cursor used by Durable Object SQLite. */
export function makeFromSource(source: SqlExecutorSource): SqlExecutor {
  return make({
    exec: <Row extends SqlRow = SqlRow>(query: string, ...bindings: SqlBinding[]) =>
      Effect.try({
        try: () => Array.from(source.exec(query, ...bindings)) as Row[],
        catch: (cause) =>
          new SqlExecutorError({
            method: 'exec',
            query,
            message: 'Unable to execute SQL statement',
            cause,
          }),
      }),
  });
}

function memoryCursor(rows: ReadonlyArray<SqlRow>): SqlCursorLike {
  return rows;
}

function normalizeStatement(query: string): string {
  return query.trim().replace(/\s+/g, ' ');
}

/** Structural in-memory SQL fake shared by the memory and Cloudflare contract layers. */
export function makeMemorySqlExecutorSource(): SqlExecutorSource {
  const contractItems = new Map<number, string>();
  const meta = new Map<string, string>();
  const nodes = new Map<string, Record<string, unknown>>();
  const edges: Array<Record<string, unknown>> = [];
  const documentLog: Array<Record<string, unknown>> = [];
  const vendorLog: Array<Record<string, unknown>> = [];
  const findings: Array<Record<string, unknown>> = [];
  const pageLedger: Array<Record<string, unknown>> = [];
  const nodesFts = new Map<string, Record<string, unknown>>();
  const vendorRaw: Array<Record<string, unknown>> = [];
  const collections = new Map<string, Record<string, unknown>>();
  const collectionDocuments: Array<Record<string, unknown>> = [];
  const documents = new Map<string, Record<string, unknown>>();
  let contractTableExists = false;
  let lastInsertRowId = 0;

  const countRow = (cnt: number): SqlCursorLike => memoryCursor([{ cnt }]);
  const latestChain = (rows: ReadonlyArray<Record<string, unknown>>): SqlCursorLike => {
    const row = [...rows].reverse().find((candidate) => candidate.chain_hash != null);
    return memoryCursor(row === undefined ? [] : [{ chain_hash: row.chain_hash }]);
  };

  return {
    exec(query: string, ...bindings: SqlBinding[]): SqlCursorLike {
      const statement = normalizeStatement(query);

      if (/^DROP TABLE IF EXISTS contract_items$/i.test(statement)) {
        contractItems.clear();
        contractTableExists = false;
        return memoryCursor([]);
      }
      if (/^CREATE TABLE contract_items \(/i.test(statement)) {
        contractItems.clear();
        contractTableExists = true;
        return memoryCursor([]);
      }
      if (/^INSERT INTO contract_items \(id, value\) VALUES \(\?, \?\)$/i.test(statement)) {
        if (!contractTableExists) throw new Error('no such table: contract_items');
        const id = Number(bindings[0]);
        const value = bindings[1];
        if (!Number.isInteger(id) || typeof value !== 'string') {
          throw new Error('invalid contract_items bindings');
        }
        contractItems.set(id, value);
        return memoryCursor([]);
      }
      if (/^SELECT id, value FROM contract_items WHERE id = \?$/i.test(statement)) {
        if (!contractTableExists) throw new Error('no such table: contract_items');
        const id = Number(bindings[0]);
        const value = contractItems.get(id);
        return memoryCursor(value === undefined ? [] : [{ id, value }]);
      }
      if (/^SELECT id, value FROM contract_items ORDER BY id$/i.test(statement)) {
        if (!contractTableExists) throw new Error('no such table: contract_items');
        return memoryCursor(
          [...contractItems.entries()]
            .sort(([left], [right]) => left - right)
            .map(([id, value]) => ({ id, value })),
        );
      }

      if (/^SELECT value FROM meta WHERE key = \?$/i.test(statement)) {
        const value = meta.get(String(bindings[0]));
        return memoryCursor(value === undefined ? [] : [{ value }]);
      }
      if (/^INSERT OR REPLACE INTO meta \(key, value\) VALUES \(\?, \?\)$/i.test(statement)) {
        meta.set(String(bindings[0]), String(bindings[1]));
        return memoryCursor([]);
      }

      if (/^INSERT OR REPLACE INTO nodes \(/i.test(statement)) {
        const [
          id, parentId, type, label, value, status, bboxX, bboxY, bboxW, bboxH,
          pageNumber, confidence, metadata, sortOrder, vendor, extractionSeq, createdAt, updatedAt,
        ] = bindings;
        nodes.set(String(id), {
          id,
          parent_id: parentId,
          type,
          label,
          value,
          status,
          bbox_x: bboxX,
          bbox_y: bboxY,
          bbox_w: bboxW,
          bbox_h: bboxH,
          page_number: pageNumber,
          confidence,
          metadata,
          sort_order: sortOrder,
          vendor,
          extraction_seq: extractionSeq,
          created_at: createdAt,
          updated_at: updatedAt,
        });
        return memoryCursor([]);
      }
      if (/^SELECT id, status, confidence, bbox_x, bbox_y FROM nodes WHERE id = \?$/i.test(statement)) {
        const node = nodes.get(String(bindings[0]));
        return memoryCursor(node === undefined ? [] : [{
          id: node.id,
          status: node.status,
          confidence: node.confidence,
          bbox_x: node.bbox_x,
          bbox_y: node.bbox_y,
        }]);
      }
      if (/^UPDATE nodes SET confidence = NULL, status = 'needs_review', updated_at = \? WHERE id = \?$/i.test(statement)) {
        const id = String(bindings[1]);
        const node = nodes.get(id);
        if (node) nodes.set(id, { ...node, confidence: null, status: 'needs_review', updated_at: bindings[0] });
        return memoryCursor([]);
      }

      if (/^DELETE FROM nodes_fts WHERE node_id = \?$/i.test(statement)) {
        nodesFts.delete(String(bindings[0]));
        return memoryCursor([]);
      }
      if (/^DELETE FROM nodes_fts$/i.test(statement)) {
        nodesFts.clear();
        return memoryCursor([]);
      }
      if (/^INSERT INTO nodes_fts .* SELECT id, label, value, type FROM nodes WHERE id = \?$/i.test(statement)) {
        const node = nodes.get(String(bindings[0]));
        if (node) {
          nodesFts.set(String(node.id), {
            node_id: node.id,
            label: node.label,
            value: node.value,
            type: node.type,
          });
        }
        return memoryCursor([]);
      }
      if (/^INSERT INTO nodes_fts .* SELECT id, label, value, type FROM nodes WHERE label IS NOT NULL OR value IS NOT NULL$/i.test(statement)) {
        for (const node of nodes.values()) {
          if (node.label != null || node.value != null) {
            nodesFts.set(String(node.id), {
              node_id: node.id,
              label: node.label,
              value: node.value,
              type: node.type,
            });
          }
        }
        return memoryCursor([]);
      }
      if (/^SELECT node_id, label, value, type FROM nodes_fts WHERE node_id = \?$/i.test(statement)) {
        const row = nodesFts.get(String(bindings[0]));
        return memoryCursor(row === undefined ? [] : [row]);
      }

      if (/^INSERT INTO edges \(/i.test(statement)) {
        edges.push({
          id: bindings[0],
          source_id: bindings[1],
          target_id: bindings[2],
          relation: bindings[3],
          metadata: null,
          created_at: bindings[4],
        });
        return memoryCursor([]);
      }
      if (/^SELECT source_id, target_id, relation FROM edges WHERE source_id = \?$/i.test(statement)) {
        return memoryCursor(
          edges
            .filter((edge) => edge.source_id === bindings[0])
            .map(({ source_id, target_id, relation }) => ({ source_id, target_id, relation })),
        );
      }

      if (/^SELECT chain_hash FROM document_log WHERE chain_hash IS NOT NULL ORDER BY seq DESC LIMIT 1$/i.test(statement)) {
        return latestChain(documentLog);
      }
      if (/^SELECT chain_hash FROM vendor_log WHERE chain_hash IS NOT NULL ORDER BY seq DESC LIMIT 1$/i.test(statement)) {
        return latestChain(vendorLog);
      }
      if (/^INSERT INTO document_log \(/i.test(statement)) {
        lastInsertRowId = documentLog.length + 1;
        documentLog.push({
          seq: lastInsertRowId,
          event: bindings[0],
          actor_type: bindings[1],
          actor_id: bindings[2],
          target_id: bindings[3],
          detail: bindings[4],
          prev_hash: bindings[5],
          chain_hash: bindings[6],
          created_at: bindings[7],
        });
        return memoryCursor([]);
      }
      if (/^SELECT event, detail FROM document_log WHERE event = 'cost' ORDER BY seq DESC LIMIT 1$/i.test(statement)) {
        const row = [...documentLog].reverse().find((candidate) => candidate.event === 'cost');
        return memoryCursor(row === undefined ? [] : [{ event: row.event, detail: row.detail }]);
      }

      if (/^INSERT INTO findings \(/i.test(statement)) {
        findings.push({
          id: bindings[0],
          node_id: bindings[1],
          rule_id: 'confidence_decay',
          severity: 'warning',
          message: 'Confidence reset after human edit — node requires re-verification',
          metadata: null,
          dismissed_at: null,
          created_at: bindings[2],
        });
        return memoryCursor([]);
      }
      if (/^SELECT id, node_id, rule_id, severity, message FROM findings WHERE node_id = \?$/i.test(statement)) {
        return memoryCursor(
          findings
            .filter((finding) => finding.node_id === bindings[0])
            .map(({ id, node_id, rule_id, severity, message }) => ({ id, node_id, rule_id, severity, message })),
        );
      }

      if (/^INSERT INTO page_ledger \(page_number, status\) VALUES \(\?, \?\)$/i.test(statement)) {
        pageLedger.push({ page_number: bindings[0], status: bindings[1] });
        return memoryCursor([]);
      }

      if (/^SELECT COUNT\(\*\) as cnt FROM nodes WHERE type != 'document' AND type != 'page'$/i.test(statement)) {
        return countRow([...nodes.values()].filter((node) => node.type !== 'document' && node.type !== 'page').length);
      }
      if (/^SELECT COUNT\(\*\) as cnt FROM nodes WHERE status = '([^']+)' AND type != 'document' AND type != 'page'$/i.test(statement)) {
        const status = statement.match(/status = '([^']+)'/i)?.[1];
        return countRow([...nodes.values()].filter(
          (node) => node.status === status && node.type !== 'document' && node.type !== 'page',
        ).length);
      }
      if (/^SELECT COUNT\(\*\) as cnt FROM nodes WHERE status IN \('needs_review', 'pending'\) AND type != 'document' AND type != 'page'$/i.test(statement)) {
        return countRow([...nodes.values()].filter(
          (node) => (node.status === 'needs_review' || node.status === 'pending')
            && node.type !== 'document'
            && node.type !== 'page',
        ).length);
      }
      if (/^SELECT COUNT\(\*\) as cnt FROM nodes WHERE type = 'page'$/i.test(statement)) {
        return countRow([...nodes.values()].filter((node) => node.type === 'page').length);
      }
      if (/^SELECT COUNT\(\*\) as cnt FROM nodes$/i.test(statement)) return countRow(nodes.size);
      if (/^SELECT COUNT\(\*\) as cnt FROM nodes_fts$/i.test(statement)) return countRow(nodesFts.size);
      if (/^SELECT COUNT\(\*\) as cnt FROM page_ledger WHERE status = '([^']+)'$/i.test(statement)) {
        const status = statement.match(/status = '([^']+)'/i)?.[1];
        return countRow(pageLedger.filter((page) => page.status === status).length);
      }
      if (/^SELECT COUNT\(\*\) as cnt FROM page_ledger$/i.test(statement)) return countRow(pageLedger.length);

      if (/^INSERT INTO vendor_raw \(/i.test(statement)) {
        lastInsertRowId = vendorRaw.length + 1;
        const hasR2Key = statement.includes('r2_storage_key');
        vendorRaw.push({
          seq: lastInsertRowId,
          vendor_log_seq: bindings[0],
          page_number: bindings[1],
          content_type: bindings[2],
          raw_response: bindings[3],
          bytes: bindings[4],
          r2_storage_key: hasR2Key ? bindings[5] : null,
          created_at: hasR2Key ? bindings[6] : bindings[5],
        });
        return memoryCursor([]);
      }
      if (/^SELECT last_insert_rowid\(\) as seq$/i.test(statement)) {
        return memoryCursor([{ seq: lastInsertRowId }]);
      }
      if (/^SELECT vendor_log_seq, raw_response, r2_storage_key FROM vendor_raw WHERE seq = \?$/i.test(statement)) {
        const row = vendorRaw.find((candidate) => candidate.seq === Number(bindings[0]));
        return memoryCursor(row === undefined ? [] : [{
          vendor_log_seq: row.vendor_log_seq,
          raw_response: row.raw_response,
          r2_storage_key: row.r2_storage_key,
        }]);
      }

      if (/^INSERT INTO collections \(/i.test(statement)) {
        const row = {
          id: String(bindings[0]),
          user_id: bindings[1],
          name: bindings[2],
          slug: bindings[3],
          description: bindings[4],
          visibility: bindings[5],
          cloned_from: bindings[6],
          allow_clone: bindings[7],
          webhook_url: bindings[8],
          extraction_schema: bindings[9],
          created_at: bindings[10],
        };
        collections.set(row.id, row);
        return memoryCursor([]);
      }
      if (/^SELECT \* FROM collections WHERE id = \?$/i.test(statement)) {
        const row = collections.get(String(bindings[0]));
        return memoryCursor(row === undefined ? [] : [row]);
      }
      if (/^SELECT id, name FROM collections WHERE id = \?$/i.test(statement)) {
        const row = collections.get(String(bindings[0]));
        return memoryCursor(row === undefined ? [] : [{ id: row.id, name: row.name }]);
      }
      if (/^SELECT name FROM collections WHERE id = \?$/i.test(statement)) {
        const row = collections.get(String(bindings[0]));
        return memoryCursor(row === undefined ? [] : [{ name: row.name }]);
      }
      if (/^INSERT INTO documents \(/i.test(statement)) {
        const row = {
          doc_id: String(bindings[0]),
          file_name: bindings[1],
          phase: bindings[2],
          pages_total: bindings[3],
          total_nodes: bindings[4],
          source: bindings[5],
          is_public: bindings[6],
        };
        documents.set(row.doc_id, row);
        return memoryCursor([]);
      }
      if (/^INSERT INTO collection_documents \(/i.test(statement)) {
        collectionDocuments.push({
          collection_id: String(bindings[0]),
          document_id: String(bindings[1]),
          added_at: bindings[2],
        });
        return memoryCursor([]);
      }
      if (/^SELECT document_id FROM collection_documents WHERE collection_id = \?$/i.test(statement)) {
        return memoryCursor(
          collectionDocuments
            .filter((row) => row.collection_id === String(bindings[0]))
            .map(({ document_id }) => ({ document_id })),
        );
      }
      if (/AND d\.is_public = 1/i.test(statement) && /FROM collection_documents cd/i.test(statement)) {
        const memberships = collectionDocuments.filter((row) => row.collection_id === String(bindings[0]));
        const publicRows = memberships.flatMap((membership) => {
          const document = documents.get(String(membership.document_id));
          if (document === undefined || Number(document.is_public) !== 1) return [];
          return [{
            document_id: membership.document_id,
            added_at: membership.added_at,
            file_name: document.file_name,
            phase: document.phase,
            pages_total: document.pages_total,
            total_nodes: document.total_nodes,
            source: document.source,
          }];
        });
        if (/SELECT cd\.document_id, cd\.added_at/i.test(statement)) return memoryCursor(publicRows);
        return memoryCursor(publicRows.map(({ document_id }) => ({ document_id })));
      }
      if (/^SELECT COUNT\(\*\) as count FROM collection_documents WHERE collection_id = \?$/i.test(statement)) {
        return memoryCursor([{
          count: collectionDocuments.filter((row) => row.collection_id === String(bindings[0])).length,
        }]);
      }

      throw new Error(`Unsupported memory SQL: ${query}`);
    },
  };
}

/**
 * Process-local SqlExecutor for contract tests.
 *
 * This intentionally implements only the tiny SQLite subset exercised by the
 * persistence contract suites.
 */
export const layerMemory: Layer.Layer<SqlExecutor> = Layer.sync(SqlExecutor)(() =>
  makeFromSource(makeMemorySqlExecutorSource()),
);
